import { displayNumberFormatter } from "./display-numbers.js";
import type { PersonalOverview } from "@cem/contracts";
import { useEffect, useState } from "react";
import { api } from "./api.js";
import { useAuth } from "./auth.js";

const today = (): string => new Date().toISOString().slice(0, 10);
const euro = (value: string): string =>
  displayNumberFormatter({ style: "currency", currency: "EUR" }).format(
    Number(value),
  );
const quantity = (value: string): string =>
  displayNumberFormatter().format(Number(value));
const italianDate = (value: string): string => {
  const [year, month, day] = value.slice(0, 10).split("-");
  return year !== undefined && month !== undefined && day !== undefined
    ? `${day}/${month}/${year}`
    : value;
};

export default function MaintenancePage() {
  const auth = useAuth();
  const canWrite = auth.user?.permissions.includes("personal:write") === true;
  const [overview, setOverview] = useState<PersonalOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState({
    customerName: "",
    title: "",
    openedOn: today(),
    hourlyRate: "",
  });
  const [timeEntry, setTimeEntry] = useState({
    personalJobId: "",
    workedOn: today(),
    hours: "",
    activity: "",
  });
  const refresh = () =>
    api.get<PersonalOverview>("/api/v1/personal/overview").then(setOverview);

  useEffect(() => {
    void refresh().catch((cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Area non disponibile"),
    );
  }, []);

  const createJob = async () => {
    const rate =
      job.hourlyRate.trim() === ""
        ? null
        : Number(job.hourlyRate.replace(",", "."));
    if (
      job.customerName.trim() === "" ||
      job.title.trim() === "" ||
      job.openedOn === "" ||
      (rate !== null && (!Number.isFinite(rate) || rate < 0))
    ) {
      setError("Compilare cliente, lavoro e una tariffa valida.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setOverview(
        await api.post<PersonalOverview>("/api/v1/personal/jobs", {
          operationId: crypto.randomUUID(),
          managementYear: Number(job.openedOn.slice(0, 4)),
          customerName: job.customerName.trim(),
          title: job.title.trim(),
          openedOn: job.openedOn,
          hourlyRate: rate,
        }),
      );
      setJob({
        customerName: "",
        title: "",
        openedOn: today(),
        hourlyRate: "",
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Commessa non creata");
    } finally {
      setBusy(false);
    }
  };

  const addTimeEntry = async () => {
    const hours = Number(timeEntry.hours.replace(",", "."));
    if (
      timeEntry.personalJobId === "" ||
      timeEntry.workedOn === "" ||
      !Number.isFinite(hours) ||
      hours <= 0 ||
      hours > 24
    ) {
      setError("Scegliere la commessa e inserire un numero di ore valido.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setOverview(
        await api.post<PersonalOverview>(
          `/api/v1/personal/jobs/${timeEntry.personalJobId}/time-entries`,
          {
            operationId: crypto.randomUUID(),
            workedOn: timeEntry.workedOn,
            hours,
            activity: timeEntry.activity.trim() || null,
          },
        ),
      );
      setTimeEntry((current) => ({
        ...current,
        hours: "",
        activity: "",
      }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Ore non registrate");
    } finally {
      setBusy(false);
    }
  };

  const assignCemTime = async (
    entryId: string,
    personalJobId: string | null,
  ) => {
    setBusy(true);
    setError(null);
    try {
      setOverview(
        await api.put<PersonalOverview>(
          `/api/v1/personal/cem-time-entries/${entryId}/assignment`,
          {
            operationId: crypto.randomUUID(),
            personalJobId,
            billingRate: null,
          },
        ),
      );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Assegnazione non salvata",
      );
    } finally {
      setBusy(false);
    }
  };

  if (overview === null)
    return (
      <section className="personal-page">
        <h1>Area personale amministratore</h1>
        <p>{error ?? "Caricamento area riservata…"}</p>
      </section>
    );

  return (
    <section className="personal-page">
      <header className="personal-heading">
        <div>
          <span className="eyebrow">Amministrazione riservata</span>
          <h1>Registro personale</h1>
          <p>
            Ore di {overview.profile.displayName} dal{" "}
            {italianDate(overview.profile.effectiveFrom)}, escluse dai conteggi
            CEM.
          </p>
        </div>
        <div className="personal-fic-state">
          <strong>FIC personale</strong>
          <span>Canale separato · non collegato</span>
        </div>
      </header>

      <div className="personal-kpis">
        <article>
          <span>Ore da gestire</span>
          <strong>{quantity(overview.totals.pendingHours)}</strong>
        </article>
        <article>
          <span>Valore maturato</span>
          <strong>{euro(overview.totals.pendingAmount)}</strong>
        </article>
        <article>
          <span>Commesse personali</span>
          <strong>{overview.totals.personalJobs}</strong>
        </article>
      </div>

      {error === null ? null : <p className="form-error">{error}</p>}

      {canWrite ? (
        <section className="personal-card">
          <h2>Nuova commessa personale</h2>
          <div className="personal-job-form">
            <label>
              Cliente
              <input
                onChange={(event) =>
                  setJob({ ...job, customerName: event.currentTarget.value })
                }
                value={job.customerName}
              />
            </label>
            <label>
              Lavoro
              <input
                onChange={(event) =>
                  setJob({ ...job, title: event.currentTarget.value })
                }
                value={job.title}
              />
            </label>
            <label>
              Data
              <input
                onChange={(event) =>
                  setJob({ ...job, openedOn: event.currentTarget.value })
                }
                type="date"
                value={job.openedOn}
              />
            </label>
            <label>
              Tariffa oraria (€)
              <input
                inputMode="decimal"
                onChange={(event) =>
                  setJob({ ...job, hourlyRate: event.currentTarget.value })
                }
                value={job.hourlyRate}
              />
            </label>
            <button disabled={busy} onClick={() => void createJob()}>
              {busy ? "Creazione…" : "Crea commessa"}
            </button>
          </div>
        </section>
      ) : null}

      {canWrite && overview.jobs.length > 0 ? (
        <section className="personal-card">
          <h2>Registra ore su una commessa personale</h2>
          <div className="personal-time-form">
            <label>
              Commessa
              <select
                onChange={(event) =>
                  setTimeEntry({
                    ...timeEntry,
                    personalJobId: event.currentTarget.value,
                  })
                }
                value={timeEntry.personalJobId}
              >
                <option value="">Scegli la commessa</option>
                {overview.jobs
                  .filter((item) =>
                    ["draft", "open", "to-invoice"].includes(item.status),
                  )
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} · {item.customerName} · {item.title}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Data
              <input
                onChange={(event) =>
                  setTimeEntry({
                    ...timeEntry,
                    workedOn: event.currentTarget.value,
                  })
                }
                type="date"
                value={timeEntry.workedOn}
              />
            </label>
            <label>
              Ore
              <input
                inputMode="decimal"
                onChange={(event) =>
                  setTimeEntry({
                    ...timeEntry,
                    hours: event.currentTarget.value,
                  })
                }
                placeholder="Es. 4,5"
                value={timeEntry.hours}
              />
            </label>
            <label className="personal-time-activity">
              Attività
              <input
                onChange={(event) =>
                  setTimeEntry({
                    ...timeEntry,
                    activity: event.currentTarget.value,
                  })
                }
                placeholder="Descrizione del lavoro svolto"
                value={timeEntry.activity}
              />
            </label>
            <button disabled={busy} onClick={() => void addTimeEntry()}>
              {busy ? "Salvataggio…" : "Salva ore"}
            </button>
          </div>
        </section>
      ) : null}

      <section className="personal-card">
        <h2>Commesse personali</h2>
        <div className="personal-jobs-list">
          {overview.jobs.length === 0 ? (
            <p>Nessuna commessa personale creata.</p>
          ) : (
            overview.jobs.map((item) => (
              <article className="personal-job-row" key={item.id}>
                <div>
                  <strong>{item.code}</strong>
                  <h3>{item.title}</h3>
                  <span>{item.customerName}</span>
                </div>
                <span>{quantity(item.totalHours)} ore</span>
                <strong>{euro(item.billableAmount)}</strong>
              </article>
            ))
          )}
        </div>
      </section>

      <section className="personal-card">
        <h2>Ore estratte dalle commesse CEM</h2>
        <div className="personal-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Commessa CEM</th>
                <th>Attività</th>
                <th>Ore</th>
                <th>Valore</th>
                <th>Commessa personale</th>
              </tr>
            </thead>
            <tbody>
              {overview.cemTimeEntries.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    Nessuna ora personale dal{" "}
                    {italianDate(overview.profile.effectiveFrom)}.
                  </td>
                </tr>
              ) : (
                overview.cemTimeEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{italianDate(entry.workedOn)}</td>
                    <td>
                      <strong>{entry.sourceJobCode}</strong>
                      <small>{entry.sourceJobTitle}</small>
                    </td>
                    <td>{entry.activity ?? "—"}</td>
                    <td>{quantity(entry.hours)}</td>
                    <td>{euro(entry.billableAmount)}</td>
                    <td>
                      {canWrite ? (
                        <select
                          aria-label={`Commessa personale per ${entry.sourceJobCode} del ${italianDate(entry.workedOn)}`}
                          disabled={busy}
                          onChange={(event) =>
                            void assignCemTime(
                              entry.id,
                              event.currentTarget.value || null,
                            )
                          }
                          value={entry.personalJobId ?? ""}
                        >
                          <option value="">Da assegnare</option>
                          {overview.jobs.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.code} · {item.title}
                            </option>
                          ))}
                        </select>
                      ) : (
                        (overview.jobs.find(
                          (item) => item.id === entry.personalJobId,
                        )?.code ?? "Da assegnare")
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="personal-card">
        <h2>FIC personale</h2>
        <p>{overview.fic.message}</p>
        <button disabled>Collega FIC personale</button>
      </section>
    </section>
  );
}
